import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, assetSrc } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { fmtCurrencyPrecise } from "@/lib/format";
import { CustomerCreateModal } from "@/components/CustomerCreateModal";
import { SignaturePad } from "@/components/SignaturePad";
import { ArrowLeft, MagnifyingGlass, Trash, Buildings, Check, MapPin, Package, Receipt, PencilSimpleLine } from "@phosphor-icons/react";

const LABEL = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";
const TH = "p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const CARD = "border border-border bg-card p-5";
const CARD_TITLE = "mb-4 flex items-center gap-2 font-display text-base font-black tracking-tight text-foreground";

export default function OrderCreatePage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", coop_code: "", contact_name: "", email: "", email2: "", phone: "", po_number: "", note_interne: "", note_client: "", customer_id: null });
  const [ship, setShip] = useState({ address: "", city: "", province: "", postal: "" });
  const [billAddr, setBillAddr] = useState({ address: "", city: "", province: "", postal: "" });
  const [shipAddrs, setShipAddrs] = useState([]);
  const [shipChoice, setShipChoice] = useState("billing");
  const [items, setItems] = useState([]);
  const [cq, setCq] = useState(""); const [cres, setCres] = useState([]); const [copen, setCopen] = useState(false);
  const [pq, setPq] = useState(""); const [pres, setPres] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [priceMap, setPriceMap] = useState({ by_id: {}, by_code: {} });
  const [sibs, setSibs] = useState([]);
  const [succId, setSuccId] = useState(null);
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [signing, setSigning] = useState(false);

  const priceFor = (pid, code, pm) => {
    const m = pm || priceMap;
    if (m.by_id && m.by_id[pid] != null) return m.by_id[pid];
    if (m.by_code && code && m.by_code[code] != null) return m.by_code[code];
    return 0;
  };

  useEffect(() => {
    if (!copen) return;
    const tm = setTimeout(() => axios.get(`${API}/customers`, { params: cq ? { q: cq } : {} }).then((r) => setCres(r.data.slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(tm);
  }, [cq, copen]);
  useEffect(() => {
    if (!pq) { setPres([]); return; }
    const tm = setTimeout(() => axios.get(`${API}/admin/order-products`, { params: { q: pq } }).then((r) => setPres(r.data.products || [])).catch(() => {}), 250);
    return () => clearTimeout(tm);
  }, [pq]);

  const pickClient = async (c) => {
    setForm({ business_name: c.business_name || "", coop_code: c.coop_client_code || "", contact_name: c.general_contact || "", email: c.email || "", email2: c.email2 || "", phone: c.phone || "", po_number: "", note_interne: "", note_client: "", customer_id: c.id });
    const bill = { address: c.address || "", city: c.city || "", province: c.province || "", postal: c.postal_code || "" };
    setBillAddr(bill); setShip({ ...bill }); setShipAddrs(c.ship_addresses || []); setShipChoice("billing");
    setCq(c.entreprise || c.business_name || ""); setCopen(false); setCres([]);
    setSuccId(c.id);
    axios.get(`${API}/customers/${c.id}/succursales`).then((r) => setSibs(r.data || [])).catch(() => setSibs([]));
    try {
      const { data } = await axios.get(`${API}/admin/customers/${c.id}/prices`);
      setPriceMap(data);
      setItems((its) => its.map((it) => (it.auto_price || !it.unit_price || parseFloat(it.unit_price) === 0)
        ? { ...it, unit_price: priceFor(it.product_id, it.product_code, data), auto_price: true } : it));
    } catch (e) { console.error("Failed to load customer prices:", e); }
  };
  const pickSuccursale = (id) => {
    const s = sibs.find((x) => x.id === id);
    if (!s) return;
    setSuccId(id);
    setForm((f) => ({ ...f, business_name: s.succursale || s.business_name || "", coop_code: s.coop_client_code || f.coop_code, customer_id: s.id }));
    const bill = { address: s.address || "", city: s.city || "", province: s.province || "", postal: s.postal_code || "" };
    setBillAddr(bill); setShip({ ...bill }); setShipAddrs(s.ship_addresses || []); setShipChoice("billing");
    axios.get(`${API}/admin/customers/${s.id}/prices`).then(({ data }) => {
      setPriceMap(data);
      setItems((its) => its.map((it) => {
        const auto = it.auto_price || !(parseFloat(it.unit_price) || 0);
        return auto ? { ...it, unit_price: priceFor(it.product_id, it.product_code, data), auto_price: true } : it;
      }));
    }).catch(() => {});
  };
  const applyShipChoice = (val) => {
    setShipChoice(val);
    if (val === "billing") setShip({ ...billAddr });
    else if (val !== "other") {
      const a = shipAddrs[Number(val)];
      if (a) setShip({ address: a.address || "", city: a.city || "", province: a.province || "", postal: a.postal || "" });
    }
  };
  const addProduct = (p) => {
    if (!items.some((it) => it.product_id === p.product_id))
      setItems([...items, { product_id: p.product_id, product_code: p.product_code, name: p.name[lang] || p.name.fr, variant_value: p.variant_value || "", variation_label: p.variation_label || "", image: p.image || "", qty: 1, unit_price: priceFor(p.product_id, p.product_code), auto_price: true, tax_gst: true, tax_qst: true, coop_category: "0238" }]);
    setPq(""); setPres([]);
  };
  const setItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    let sub = 0, g = 0, q = 0;
    items.forEach((it) => { const lt = (parseFloat(it.unit_price) || 0) * (parseFloat(it.qty) || 0); sub += lt; if (it.tax_gst) g += lt; if (it.tax_qst) q += lt; });
    return { sub, gst: g * 0.05, qst: q * 0.09975, total: sub + g * 0.05 + q * 0.09975 };
  }, [items]);

  const create = async () => {
    if (!form.business_name.trim()) { toast.error(t.businessName + " *"); return; }
    if (items.length === 0) { toast.error(t.noItems); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders`, {
        ...form, ship_address: ship.address, ship_city: ship.city, ship_province: ship.province, ship_postal: ship.postal,
        signature_required: signatureRequired,
        items: items.map((it) => ({ product_id: it.product_id, product_code: it.product_code, name: it.name, variant_value: it.variant_value || "", variation_label: it.variation_label || "", qty: parseFloat(it.qty) || 1, unit_price: parseFloat(it.unit_price) || 0, tax_gst: true, tax_qst: true, coop_category: "0238" })),
      });
      toast.success(t.orderCreated);
      if (signatureRequired) { setCreatedId(data.id); setSigOpen(true); }
      else navigate(`/admin/orders/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setSaving(false); }
  };

  const submitSignature = async (dataUrl, signerName) => {
    setSigning(true);
    try {
      await axios.post(`${API}/admin/orders/${createdId}/signature`, { image: dataUrl, signer_name: signerName });
      toast.success(t.signatureSaved);
      setSigOpen(false);
      navigate(`/admin/orders/${createdId}`);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setSigning(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
        {/* Top bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <button data-testid="back-to-orders" onClick={() => navigate("/admin/orders")} className="mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200">
              <ArrowLeft size={16} weight="bold" /> {t.orders}
            </button>
            <h1 className="font-display text-3xl font-black tracking-tighter text-foreground">{t.newOrder}</h1>
          </div>
          <button data-testid="create-order-button" onClick={create} disabled={saving} className="inline-flex items-center gap-2 bg-primary px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0">
            <Check size={16} weight="bold" /> {t.createOrder}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* LEFT — Status, Customer & addresses */}
          <div className="space-y-5 lg:col-span-4">
            {/* Status */}
            <div className={CARD}>
              <div className={CARD_TITLE}><Receipt size={18} weight="bold" className="text-primary" />{t.status}</div>
              <div className="inline-flex items-center gap-2 border border-border bg-background px-3 py-1.5 text-xs font-bold text-blue-600">{signatureRequired ? t.awaitingSignature : t.statusOpen}</div>
              <label className="mt-4 flex cursor-pointer items-start gap-2.5">
                <input type="checkbox" data-testid="require-signature" checked={signatureRequired} onChange={(e) => setSignatureRequired(e.target.checked)} className="mt-0.5 h-4 w-4" />
                <span className="text-sm text-foreground">
                  <span className="flex items-center gap-1.5 font-semibold"><PencilSimpleLine size={15} weight="bold" className="text-primary" />{t.requireSignature}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{t.requireSignatureHint}</span>
                </span>
              </label>
            </div>

            {/* Customer information */}
            <div className={CARD}>
              <div className={CARD_TITLE}><Buildings size={18} weight="bold" className="text-primary" />{t.customerInformation}</div>
              <div className="relative">
                <span className={LABEL}>{t.client} *</span>
                <div className="flex items-center border border-border bg-background px-3 focus-within:border-primary">
                  <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
                  <input data-testid="client-search" value={cq} onChange={(e) => { setCq(e.target.value); setCopen(true); setForm((f) => ({ ...f, customer_id: null })); }} onFocus={() => setCopen(true)} onBlur={() => setTimeout(() => setCopen(false), 150)} placeholder={t.searchClient} className="w-full bg-transparent px-2 py-2.5 text-sm outline-none" />
                </div>
                {copen && (
                  <div data-testid="client-dropdown" className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto border border-border bg-card shadow-lg">
                    {cres.map((c) => (
                      <button key={c.id} data-testid={`client-opt-${c.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pickClient(c)} className="block w-full px-3 py-2 text-left text-sm hover:bg-primary hover:text-white transition-colors duration-100">
                        <span className="font-medium">{c.entreprise || c.business_name}</span>{c.entreprise && c.succursale && c.succursale !== c.entreprise && <span className="opacity-80"> · {c.succursale}</span>} <span className="text-xs opacity-70">{c.coop_client_code} · {c.email}</span>
                      </button>
                    ))}
                    <button data-testid="open-create-customer" onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowCreate(true); setCopen(false); }} className="block w-full border-t border-border px-3 py-2 text-left text-sm font-semibold text-primary hover:bg-muted">+ {t.createClientPrompt}</button>
                  </div>
                )}
              </div>
              {sibs.length > 1 && (
                <div className="mt-3">
                  <span className={LABEL}>{t.selectSuccursale}</span>
                  <select data-testid="succursale-select" value={succId || ""} onChange={(e) => pickSuccursale(e.target.value)}
                    className="w-full border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                    {sibs.map((s) => <option key={s.id} value={s.id}>{s.succursale}{s.city ? ` — ${s.city}` : ""}</option>)}
                  </select>
                </div>
              )}
              {form.coop_code && (
                <div className="mt-2 text-xs text-muted-foreground">{t.coopClientCode}: <span className="font-mono text-foreground">{form.coop_code}</span></div>
              )}
              <div className="mt-3 space-y-3">
                <div><span className={LABEL}>{t.email}</span><input data-testid="of-email" className={INPUT} placeholder={t.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><span className={LABEL}>{t.email2}</span><input data-testid="of-email2" className={INPUT} placeholder={t.email2} value={form.email2} onChange={(e) => setForm({ ...form, email2: e.target.value })} /></div>
                <div><span className={LABEL}>{t.phone}</span><input data-testid="of-phone" className={INPUT} placeholder={t.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><span className={LABEL}>{t.poNumber}</span><input data-testid="of-po-number" className={INPUT} placeholder={t.poNumberHint} value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></div>
              </div>
            </div>

            {/* Shipping address */}
            <div className={CARD} data-testid="ship-block">
              <div className={CARD_TITLE}><MapPin size={18} weight="bold" className="text-primary" />{t.shippingAddress}</div>
              <div className="space-y-3">
                <div>
                  <span className={LABEL}>{t.chooseShipAddress}</span>
                  <select data-testid="ship-choice" className={INPUT} value={shipChoice} onChange={(e) => applyShipChoice(e.target.value)}>
                    <option value="billing">{t.useBillingAddress}</option>
                    {shipAddrs.map((a, i) => <option key={i} value={String(i)}>{a.label || `${t.shippingAddress} ${i + 1}`}</option>)}
                    <option value="other">{t.otherManualAddress}</option>
                  </select>
                </div>
                <div><span className={LABEL}>{t.shippingAddress}</span><input data-testid="ship-address" className={INPUT} placeholder={t.shippingAddress} value={ship.address} onChange={(e) => { setShip({ ...ship, address: e.target.value }); setShipChoice("other"); }} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><span className={LABEL}>{t.shipCity}</span><input data-testid="ship-city" className={INPUT} placeholder={t.shipCity} value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} /></div>
                  <div><span className={LABEL}>{t.shipProvince}</span><input data-testid="ship-province" className={INPUT} placeholder={t.shipProvince} value={ship.province} onChange={(e) => setShip({ ...ship, province: e.target.value })} /></div>
                </div>
                <div><span className={LABEL}>{t.shipPostal}</span><input data-testid="ship-postal" className={`${INPUT} font-mono`} placeholder={t.shipPostal} value={ship.postal} onChange={(e) => setShip({ ...ship, postal: e.target.value })} /></div>
              </div>
            </div>
          </div>

          {/* RIGHT — Product selection (enlarged) */}
          <div className="space-y-5 lg:col-span-8">
            <div className="relative">
              <span className={LABEL}>{t.addProduct}</span>
              <div className="flex items-center border-2 border-border bg-background px-4 focus-within:border-primary transition-colors duration-200">
                <MagnifyingGlass size={20} weight="bold" className="text-muted-foreground" />
                <input data-testid="product-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder={t.searchProduct} className="w-full bg-transparent px-3 py-4 text-base outline-none" />
              </div>
              {pres.length > 0 && (
                <div data-testid="product-dropdown" className="absolute z-30 mt-1 max-h-96 w-full overflow-y-auto border border-border bg-card shadow-lg">
                  {pres.map((p) => (
                    <button key={p.product_id} data-testid={`prod-opt-${p.product_id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => addProduct(p)} className="group flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-primary hover:text-white transition-colors duration-100">
                      <div className="h-11 w-11 shrink-0 border border-border bg-muted">
                        {p.image
                          ? <img src={assetSrc(p.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={18} /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium leading-snug">{p.name[lang] || p.name.fr}
                          {p.status === "H" && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 group-hover:bg-white/20 group-hover:text-white">{t.statusHidden}</span>}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2">
                          <span className="font-mono text-[11px] opacity-70">{p.product_code}</span>
                          {p.variant_value && <span data-testid={`variant-badge-${p.product_id}`} className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary group-hover:bg-white/20 group-hover:text-white">{p.variation_label ? `${p.variation_label}: ` : ""}{p.variant_value}</span>}
                        </div>
                      </div>
                      <div className="shrink-0 text-xs opacity-80">{p.quantity ?? 0} {t.inStock}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Items table — Product / Price / Quantity / Total */}
            <div className="border border-border bg-card" data-testid="new-order-items">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-border text-left">
                    <th className={TH}>{t.product || "Produit"}</th>
                    <th className={`${TH} text-right`}>{t.unitPrice} ($)</th>
                    <th className={`${TH} text-center`}>{t.quantity}</th>
                    <th className={`${TH} text-right`}>{t.grandTotal}</th>
                    <th className={TH}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">{t.noItems}</td></tr>
                  )}
                  {items.map((it, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="w-[55%] p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 shrink-0 border border-border bg-muted">
                            {it.image
                              ? <img src={assetSrc(it.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={16} /></div>}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium leading-snug text-foreground">{it.name}</div>
                            <div className="mt-0.5 flex items-center gap-2">
                              <span className="font-mono text-[11px] text-primary">{it.product_code}</span>
                              {it.variant_value && <span data-testid={`item-variant-badge-${it.product_id}`} className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">{it.variation_label ? `${it.variation_label}: ` : ""}{it.variant_value}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-right"><span className="inline-flex items-center gap-1"><input data-testid={`price-input-${i}`} type="number" step="0.01" min="0" value={it.unit_price ?? ""} onChange={(e) => setItem(i, { unit_price: e.target.value, auto_price: false })} className="w-24 border border-border bg-background px-2 py-1 text-right text-sm outline-none focus:border-primary" /> $</span></td>
                      <td className="p-3 text-center"><input data-testid={`qty-${i}`} type="number" step="0.01" min="0" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="w-20 border border-border bg-background px-2 py-1 text-center text-sm outline-none focus:border-primary" /></td>
                      <td className="p-3 text-right font-semibold tabular-nums text-foreground">{fmtCurrencyPrecise((parseFloat(it.unit_price) || 0) * (parseFloat(it.qty) || 0), lang)}</td>
                      <td className="p-3 text-center"><button data-testid={`remove-${i}`} onClick={() => removeItem(i)} className="text-muted-foreground hover:text-primary"><Trash size={16} weight="bold" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals — under products, right-aligned */}
            <div className="flex justify-end">
              <div className="w-full max-w-xs" data-testid="order-totals">
                <div className="mb-3 border-b-2 border-border pb-2 text-right font-display text-lg font-black tracking-tight text-foreground">{t.orderSummary}</div>
                <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{t.subtotal}</span><span className="tabular-nums">{fmtCurrencyPrecise(totals.sub, lang)}</span></div>
                <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{t.gstShort}</span><span className="tabular-nums">{fmtCurrencyPrecise(totals.gst, lang)}</span></div>
                <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{t.qstShort}</span><span className="tabular-nums">{fmtCurrencyPrecise(totals.qst, lang)}</span></div>
                <div className="mt-2 flex justify-between border-t border-border pt-3 font-bold"><span>{t.grandTotal}</span><span className="tabular-nums text-primary" data-testid="new-order-total">{fmtCurrencyPrecise(totals.total, lang)}</span></div>
              </div>
            </div>

            {/* Notes: interne + client, cote a cote */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className={LABEL}><Receipt size={12} weight="bold" className="mr-1 inline" />{t.noteInterne}</span>
                <textarea data-testid="of-note-interne" className={`${INPUT} min-h-[100px] resize-y`} placeholder={t.noteInterneHint} value={form.note_interne || ""} onChange={(e) => setForm({ ...form, note_interne: e.target.value })} />
              </div>
              <div>
                <span className={LABEL}><Receipt size={12} weight="bold" className="mr-1 inline" />{t.noteClient}</span>
                <textarea data-testid="of-note-client" className={`${INPUT} min-h-[100px] resize-y`} placeholder={t.noteClientHint} value={form.note_client || ""} onChange={(e) => setForm({ ...form, note_client: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        <CustomerCreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={(c) => pickClient(c)} initialName={cq} />
        <SignaturePad open={sigOpen} onClose={() => { setSigOpen(false); if (createdId) navigate(`/admin/orders/${createdId}`); }} onSave={submitSignature} defaultName={form.contact_name} saving={signing} />
      </main>
    </div>
  );
}
